/**
 * 解析后的消息片段类型
 */
export interface ParsedPart {
  type: 'text' | 'planning' | 'replanning' | 'reasoning' | 'action_log' | 'final_result' | 'process_text';
  content: string;
}

/**
 * 解析规则配置
 */
export interface ParseAgentMessageOptions {
  /**
   * 解析模式
   * - 'xml': XML 标签模式 <reasoning>...</reasoning>
   * - 'comment': 注释标签模式 /*REASONING*\/
   * - 'custom': 自定义正则模式
   */
  mode?: 'xml' | 'comment' | 'custom';

  /**
   * 自定义正则规则 (mode='custom' 时使用)
   * @example
   * [
   *   { regex: /\[REASONING\]([\s\S]*?)\[\/REASONING\]/g, type: 'reasoning' },
   *   { regex: /\[PLANNING\]([\s\S]*?)\[\/PLANNING\]/g, type: 'planning' }
   * ]
   */
  customPatterns?: Array<{
    regex: RegExp;
    type: ParsedPart['type'];
  }>;

  /**
   * 最终答案标记 (comment 模式使用)
   * @default '/*FINAL_ANSWER*\/'
   */
  finalMarker?: string;
}

/**
 * 清理 process 文本
 * - 移除多余的换行
 * - 移除 JSON 代码块标记
 */
export function cleanProcessText(text: string): string {
  return text
    .replace(/```json\s*/g, '')
    .replace(/```\s*/g, '')
    .replace(/^\n+/, '')
    .replace(/\n+$/, '')
    .trim();
}

/**
 * XML 模式解析 (默认)
 * 识别标签: <reasoning>、<planning>、<replanning>、<action_log>
 */
function parseXmlMode(text: string): ParsedPart[] {
  const parts: ParsedPart[] = [];

  // 定义标签模式
  const tagPatterns: Array<{
    regex: RegExp;
    type: ParsedPart['type'];
  }> = [
    { regex: /<reasoning>([\s\S]*?)<\/reasoning>/g, type: 'reasoning' },
    { regex: /<planning>([\s\S]*?)<\/planning>/g, type: 'planning' },
    { regex: /<replanning>([\s\S]*?)<\/replanning>/g, type: 'replanning' },
    { regex: /<action_log>([\s\S]*?)<\/action_log>/g, type: 'action_log' },
  ];

  // 收集所有标签位置
  interface TagMatch {
    type: ParsedPart['type'];
    start: number;
    end: number;
    content: string;
  }

  const matches: TagMatch[] = [];

  tagPatterns.forEach(({ regex, type }) => {
    let match;
    const tempRegex = new RegExp(regex);
    while ((match = tempRegex.exec(text)) !== null) {
      matches.push({
        type,
        start: match.index,
        end: match.index + match[0].length,
        content: match[1].trim(),
      });
    }
  });

  // 按位置排序
  matches.sort((a, b) => a.start - b.start);

  // 提取文本和标签
  let currentPos = 0;

  matches.forEach((match) => {
    // 添加标签前的文本
    if (match.start > currentPos) {
      const textBefore = text.substring(currentPos, match.start).trim();
      if (textBefore) {
        parts.push({ type: 'text', content: textBefore });
      }
    }

    // 添加标签内容
    parts.push({ type: match.type, content: match.content });

    currentPos = match.end;
  });

  // 添加最后的文本
  if (currentPos < text.length) {
    const textAfter = text.substring(currentPos).trim();
    if (textAfter) {
      parts.push({ type: 'text', content: textAfter });
    }
  }

  return parts;
}

/**
 * 注释模式解析 (/*PLANNING*\/ 格式)
 * 适用于业务项目的格式
 */
function parseCommentMode(text: string, finalMarker: string = '/*FINAL_ANSWER*/'): ParsedPart[] {
  // 如果完全没有 Agent 推理标记，则整段文本视为最终结果
  if (!text.includes('/*')) {
    return [{ type: 'final_result', content: text }];
  }

  const results: ParsedPart[] = [];
  const finalIndex = text.indexOf(finalMarker);

  let processText = text;
  let finalText = '';

  if (finalIndex !== -1) {
    processText = text.substring(0, finalIndex);
    finalText = text.substring(finalIndex + finalMarker.length);
  }

  const regex = /(\/\*PLANNING\*\/|\/\*REPLANNING\*\/|\/\*REASONING\*\/|\/\*ACTION\*\/)/g;
  const parts = processText.split(regex);

  let currentType: ParsedPart['type'] = 'process_text';

  parts.forEach((part) => {
    if (part === '/*PLANNING*/') {
      currentType = 'planning';
      return;
    }
    if (part === '/*REPLANNING*/') {
      currentType = 'replanning';
      return;
    }
    if (part === '/*REASONING*/') {
      currentType = 'reasoning';
      return;
    }
    if (part === '/*ACTION*/') {
      currentType = 'action_log';
      return;
    }

    if (part && part.trim().length > 0) {
      results.push({ type: currentType, content: part });
    }
  });

  if (finalText.trim()) {
    results.push({ type: 'final_result', content: finalText });
  }

  return results;
}

/**
 * 自定义模式解析
 */
function parseCustomMode(text: string, patterns: Array<{ regex: RegExp; type: ParsedPart['type'] }>): ParsedPart[] {
  const parts: ParsedPart[] = [];
  const matches: Array<{ type: ParsedPart['type']; start: number; end: number; content: string }> = [];

  patterns.forEach(({ regex, type }) => {
    let match;
    const tempRegex = new RegExp(regex);
    while ((match = tempRegex.exec(text)) !== null) {
      matches.push({
        type,
        start: match.index,
        end: match.index + match[0].length,
        content: match[1]?.trim() || match[0],
      });
    }
  });

  matches.sort((a, b) => a.start - b.start);

  let currentPos = 0;
  matches.forEach((match) => {
    if (match.start > currentPos) {
      const textBefore = text.substring(currentPos, match.start).trim();
      if (textBefore) {
        parts.push({ type: 'text', content: textBefore });
      }
    }
    parts.push({ type: match.type, content: match.content });
    currentPos = match.end;
  });

  if (currentPos < text.length) {
    const textAfter = text.substring(currentPos).trim();
    if (textAfter) {
      parts.push({ type: 'text', content: textAfter });
    }
  }

  return parts;
}

/**
 * 解析 Agent 消息 (支持多种格式)
 *
 * @param text 原始消息文本
 * @param options 解析选项
 * @returns 解析后的消息片段数组
 *
 * @example
 * // XML 模式 (默认)
 * const parts1 = parseAgentMessage(`
 *   <reasoning>分析用户需求...</reasoning>
 *   最终回复内容
 * `);
 *
 * @example
 * // 注释模式 (业务项目格式)
 * const parts2 = parseAgentMessage(`
 *   /*REASONING*\/分析用户需求...
 *   /*FINAL_ANSWER*\/最终回复内容
 * `, { mode: 'comment' });
 *
 * @example
 * // 自定义模式
 * const parts3 = parseAgentMessage(text, {
 *   mode: 'custom',
 *   customPatterns: [
 *     { regex: /\[REASONING\]([\s\S]*?)\[\/REASONING\]/g, type: 'reasoning' }
 *   ]
 * });
 */
export function parseAgentMessage(text: string, options: ParseAgentMessageOptions = {}): ParsedPart[] {
  if (!text) return [];

  const { mode = 'xml', customPatterns, finalMarker } = options;

  // 根据模式选择解析器
  if (mode === 'comment') {
    return parseCommentMode(text, finalMarker);
  }

  if (mode === 'custom' && customPatterns) {
    return parseCustomMode(text, customPatterns);
  }

  // 默认 XML 模式
  const parts = parseXmlMode(text);

  // 如果没有任何标签,返回整个文本
  if (parts.length === 0) {
    return [{ type: 'text', content: text }];
  }

  return parts;
}

/**
 * 检查是否包含 process 内容
 */
export function hasProcessContent(parts: ParsedPart[]): boolean {
  return parts.some((part) =>
    ['planning', 'replanning', 'reasoning', 'action_log', 'process_text'].includes(part.type)
  );
}

/**
 * 获取纯文本内容(不包含 process 标签)
 */
export function getPlainText(parts: ParsedPart[]): string {
  return parts
    .filter((part) => part.type === 'text' || part.type === 'final_result')
    .map((part) => part.content)
    .join('\n\n')
    .trim();
}
